/**
 * Journey: Embryo Freezing, With Partner, Female
 *
 * 7-section structure:
 *   1. Complete Medical History — persistent prerequisite, unlocks personalization
 *   2. Understand — education about embryo freezing with a partner
 *   3. Know Your Numbers — all testing/labs for both partners
 *   4. Optimize Before Care — supplements + lifestyle (early because they take months)
 *   5. Insurance & Coverage — know what's covered before choosing a clinic
 *   6. Find Care — clinic search, costs, and next steps
 *   7. Start Your Cycle — consent forms, start date, active treatment
 *
 * Partner fills their own medical history on their own account.
 * Partner-specific info (testing, optimization) is shared via links, not tracked here.
 */

import type { JourneyDefinition, PlanStep } from "../plan.types";

// ─── 1. Complete Medical History ───

const medicalHistoryStep: PlanStep = {
  id: "medical-history",
  title: "Complete medical history",
  subtitle: "Unlocks personalized guidance for everything below",
  icon: "FileText",
  education: {
    context:
      "Your medical history drives everything, from which tests you need to how your protocol is designed. The more complete it is, the better your plan becomes. Your partner will fill out their own history on their account.",
  },
  actions: [
    {
      label: "Continue medical history",
      variant: "primary",
      screen: "MedicalHistory",
    },
  ],
  completionSignal: {
    type: "completion-percent",
    field: "medicalHistoryProgress",
    threshold: 100,
  },
};

// ─── 2. Understand ───

const understandStep: PlanStep = {
  id: "understand",
  title: "Understand embryo freezing",
  subtitle: "What happens and what to expect, for both of you",
  icon: "BookOpen",
  education: {
    context:
      "Embryo freezing combines your eggs with your partner's sperm to create embryos that are frozen for future use. Unlike egg freezing alone, you'll already know fertilization worked, and embryos have higher survival rates when thawed.",
    processSteps: [
      {
        who: "You",
        description:
          "Ovarian stimulation (10-14 days of injections) followed by egg retrieval",
        keywords: [
          {
            term: "ovarian stimulation",
            articleId: "moon-article-cycle-process",
          },
          { term: "egg retrieval", articleId: "moon-article-cycle-process" },
        ],
      },
      {
        who: "Your partner",
        description: "Provides a semen sample on retrieval day",
        keywords: [
          { term: "semen sample", articleId: "moon-article-embryo-freezing" },
        ],
      },
      {
        who: "The lab",
        description:
          "Fertilizes your eggs, cultures embryos for 5-7 days, then freezes the best ones",
        keywords: [
          {
            term: "cultures embryos",
            articleId: "moon-article-embryo-freezing",
          },
          { term: "PGT-A", articleId: "moon-article-pgt-testing" },
        ],
      },
    ],
    learnMoreLink: "embryo-freezing-guide",
  },
  actions: [
    {
      label: "What is embryo freezing?",
      variant: "outline",
      icon: "BookOpen",
      screen: "ArticleDetail",
      params: { articleId: "moon-article-embryo-freezing" },
    },
    {
      label: "Eggs vs embryos: help me decide",
      variant: "outline",
      icon: "ArrowLeftRight",
      screen: "ArticleDetail",
      params: { articleId: "moon-article-eggs-vs-embryos" },
    },
    {
      label: "What happens during a cycle",
      variant: "outline",
      icon: "BookOpen",
      screen: "ArticleDetail",
      params: { articleId: "moon-article-cycle-process" },
    },
  ],
  completionSignal: { type: "manual" },
  ageSpecificContent: [
    {
      range: [0, 34],
      content:
        "At your age, most women retrieve 10-20 eggs per cycle. Even with normal fertilization rates (~70-80%), one cycle often yields several quality embryos.",
    },
    {
      range: [35, 37],
      content:
        "At 35-37, egg quality begins declining. Most women retrieve 8-14 eggs. Fertilization rates remain good, but you may want to discuss banking embryos over multiple cycles with your clinic.",
    },
    {
      range: [38, 40],
      content:
        "At 38-40, both quantity and quality are declining. Expect 5-10 eggs per retrieval. PGT-A testing becomes more relevant as chromosomal abnormality rates increase. Multiple cycles are commonly recommended.",
    },
    {
      range: [41, 99],
      content:
        "After 40, fewer eggs are typically retrieved and a higher proportion may have chromosomal abnormalities. PGT-A testing helps identify viable embryos. Your clinic will discuss realistic expectations.",
    },
  ],
};

// ─── 3. Know Your Numbers ───

const knowYourNumbersStep: PlanStep = {
  id: "know-your-numbers",
  title: "Know your numbers",
  subtitle: "Your ovarian reserve + partner's sperm health",
  icon: "TestTube",
  education: {
    context:
      "Before a clinic can plan your cycle, they need to assess both your ovarian reserve and your partner's sperm health. Your results determine your stimulation protocol; your partner's results determine the fertilization method (conventional vs ICSI).",
  },
  actions: [
    {
      label: "Order at-home AMH test",
      variant: "primary",
      screen: "AtHomeFertilityTesting",
    },
    {
      label: "Share partner testing info",
      variant: "secondary",
      icon: "Share2",
      signal: "open-partner-or-donor-modal",
    },
    {
      label: "Upload test results",
      variant: "outline",
      signal: "open-document-upload",
      params: { initialCategory: "medical_records" },
    },
  ],
  substeps: [
    {
      id: "amh",
      title: "AMH (Anti-Müllerian Hormone)",
      education:
        "Measures your egg supply. It's the single most important test for protocol planning. A simple blood test that can be done at home or at any lab, any day of your cycle.",
      status: "not-started",
      actions: [
        {
          label: "Order at-home test",
          variant: "primary",
          screen: "AtHomeFertilityTesting",
        },
      ],
    },
    {
      id: "fsh-estradiol",
      title: "FSH + Estradiol",
      education:
        "Must be done on cycle day 2-3. FSH shows how hard your body is working to recruit eggs. Estradiol provides context for the FSH reading.",
      status: "not-started",
      actions: [
        {
          label: "Track your cycle",
          variant: "outline",
          icon: "CalendarDays",
          screen: "MenstrualHealthFlow",
        },
      ],
    },
    {
      id: "afc",
      title: "Antral Follicle Count (AFC)",
      education:
        "Transvaginal ultrasound at a clinic. Combined with AMH, gives the most accurate picture of expected egg yield.",
      status: "not-started",
    },
  ],
  partnerTrack: {
    education:
      "Your partner's sperm health determines whether conventional fertilization or ICSI (single sperm injection) will be used. ICSI adds ~$2,000 but is standard for any male factor concerns.",
    substeps: [
      {
        id: "partner-sa",
        title: "Semen analysis",
        education:
          "Measures count (>15M/mL normal), motility (>40% normal), and morphology (>4% normal). Results determine fertilization method.",
        status: "not-started",
        isPartnerTrack: true,
        actions: [
          {
            label: "Share testing info with partner",
            variant: "primary",
            icon: "Share2",
            signal: "open-partner-or-donor-modal",
          },
        ],
      },
    ],
  },
  completionSignal: { type: "all-substeps" },
  conditionModifiers: [
    {
      condition: "pcos",
      additionalEducation:
        "With PMOS (ex PCOS), your AMH may be higher than expected. This doesn't necessarily mean more usable eggs. Your clinic will adjust your protocol to reduce the risk of ovarian hyperstimulation (OHSS).",
    },
    {
      condition: "diminished-reserve",
      additionalEducation:
        "With diminished ovarian reserve, your AMH and AFC may be lower. Your clinic may recommend a more aggressive stimulation protocol or discuss expectations around egg yield.",
    },
  ],
};

// ─── 4. Optimize Before Care ───

const optimizeStep: PlanStep = {
  id: "optimize",
  title: "Optimize before your cycle",
  subtitle: "Things you can start now that take time to work",
  icon: "Sparkles",
  education: {
    context:
      "Egg quality is influenced by factors over the 3 months before retrieval (the time it takes for eggs to mature). Starting supplements and lifestyle changes now gives them time to take effect before your cycle.",
    details:
      "These won't make or break your outcome, but the evidence supports a meaningful edge, especially for egg quality. Your partner's sperm quality also benefits from the same timeline (~74 days for sperm regeneration).",
  },
  actions: [
    {
      label: "See recommended supplements",
      variant: "primary",
      screen: "SupplementsScreen",
      params: { goal: "egg-quality" },
    },
  ],
  substeps: [
    {
      id: "supplements-female",
      title: "Start supplements",
      education:
        "CoQ10 (400-600mg/day) supports mitochondrial function in eggs. Vitamin D, folate, and omega-3s are also commonly recommended. Start 2-3 months before your cycle for best effect.",
      status: "not-started",
      actions: [
        {
          label: "See supplements",
          variant: "primary",
          screen: "SupplementsScreen",
          params: { goal: "egg-quality" },
        },
      ],
    },
    {
      id: "lifestyle",
      title: "Lifestyle factors",
      education:
        "Sleep, moderate exercise, balanced nutrition, and limiting alcohol all support egg quality. No single change is critical. It's the pattern that matters.",
      status: "not-started",
    },
  ],
  partnerTrack: {
    education:
      "Sperm regenerates on a ~74-day cycle. Lifestyle and supplement changes now can improve sperm quality by retrieval day.",
    substeps: [
      {
        id: "partner-supplements",
        title: "Partner: start supplements",
        education:
          "CoQ10, zinc, folate, and vitamin D support sperm quality. Avoid heat exposure (hot tubs, saunas, laptops on lap), limit alcohol, and stay active.",
        status: "not-started",
        isPartnerTrack: true,
        actions: [
          {
            label: "Share with partner",
            variant: "outline",
            icon: "Share2",
            signal: "open-partner-or-donor-modal",
          },
        ],
      },
    ],
  },
  completionSignal: { type: "manual" },
};

// ─── 5. Insurance & Coverage ───

const insuranceStep: PlanStep = {
  id: "insurance",
  title: "Insurance & coverage",
  subtitle: "Know what's covered before you choose a clinic",
  icon: "ClipboardCheck",
  education: {
    context:
      "Even if your plan doesn't cover fertility treatment, diagnostics, bloodwork, and anesthesia often are. Knowing your coverage early saves surprises later and helps us recommend clinics that accept your plan.",
  },
  actions: [
    {
      label: "Scan insurance card",
      variant: "primary",
      screen: "CoverageFlow",
    },
    {
      label: "Check fertility coverage",
      variant: "outline",
      screen: "CostsOverviewScreen",
    },
  ],
  substeps: [
    {
      id: "upload-insurance",
      title: "Upload insurance card",
      education:
        "We'll extract your plan details automatically. This lets us check which clinics are in-network and estimate your out-of-pocket costs.",
      status: "not-started",
      actions: [
        {
          label: "Scan card",
          variant: "primary",
          screen: "CoverageFlow",
        },
      ],
    },
    {
      id: "verify-fertility-coverage",
      title: "Verify fertility benefits",
      education:
        "We'll check your plan for fertility-specific coverage. IVF mandates vary by state and employer.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "document-uploaded", category: "insurance-card" },
};

// ─── 6. Find Care ───

const findCareStep: PlanStep = {
  id: "find-care",
  title: "Find care",
  subtitle: "Clinic, costs, and next steps",
  icon: "Building2",
  education: {
    context:
      "Your fertility clinic manages your entire cycle, from planning your medication protocol to performing the egg retrieval and embryo creation.",
    details:
      "Things that matter when choosing:\n• Success rates: SART reports clinic-specific data\n• Location: You'll visit 4-6 times during monitoring\n• Cost: Embryo freezing costs $10,000-$18,000 (includes fertilization)\n• In-network status: We'll flag clinics that accept your plan",
  },
  actions: [
    {
      label: "Browse clinics near you",
      variant: "primary",
      screen: "FindCareScreen",
      params: { filter: "embryo-freezing" },
    },
    {
      label: "Compare clinic costs",
      variant: "outline",
      screen: "CostsOverviewScreen",
    },
  ],
  substeps: [
    {
      id: "research-clinics",
      title: "Research clinics",
      education:
        "Look at success rates, reviews, location, and whether they accept your insurance.",
      status: "not-started",
      actions: [
        {
          label: "Browse clinics",
          variant: "primary",
          screen: "FindCareScreen",
          params: { filter: "embryo-freezing" },
        },
      ],
    },
    {
      id: "schedule-consultation",
      title: "Schedule a consultation",
      education:
        "Most clinics offer a 30-60 minute initial consultation to review your case and discuss options.",
      status: "not-started",
    },
    {
      id: "choose-clinic",
      title: "I've chosen my clinic",
      education:
        "Once you've found the right fit, your clinic will start your onboarding.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "manual" },
};

// ─── 7. Start Your Cycle ───

const startCycleStep: PlanStep = {
  id: "start-cycle",
  title: "Start your cycle",
  subtitle: "Final prep and active treatment for both of you",
  icon: "Syringe",
  education: {
    context:
      "Once your clinic gives the green light, you'll sign consent forms, complete your embryo agreement, and get your start date. The embryo agreement is a legal document your clinic will walk you through. It covers ownership, decision-making, and what happens in different scenarios. From there, your daily medication protocol, monitoring schedule, and phase-by-phase guidance activates. Your partner will need to provide a semen sample on retrieval day.",
  },
  actions: [
    {
      label: "I have a start date",
      variant: "primary",
      icon: "Calendar",
      signal: "open-start-treatment-modal",
      params: { presetCycleType: "embryo_freezing" },
    },
    {
      label: "Find a clinic while you wait",
      variant: "outline",
      icon: "Clock",
      screen: "FindCareScreen",
      params: { filter: "embryo-freezing" },
    },
  ],
  substeps: [
    {
      id: "consent-forms",
      title: "Sign consent forms",
      education:
        "Your clinic provides these. They cover the procedure, risks, storage terms, and what happens to embryos in various scenarios.",
      status: "not-started",
      actions: [
        {
          label: "Upload signed consents",
          variant: "outline",
          screen: "DocumentsScreen",
          params: { initialCategory: "legal_consent" },
        },
      ],
    },
    {
      id: "set-start-date",
      title: "Confirm your start date",
      education:
        "Your clinic will coordinate timing based on your cycle. Once confirmed, your daily guidance activates.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "external" },
};

// ─── Journey Definition ───

export const EMBRYO_FREEZING_PARTNER: JourneyDefinition = {
  key: "embryo-freezing-with-partner",
  gender: "female",
  goal: "embryo-preserve",
  pathway: "with-partner",
  title: "Embryo Freezing",
  description: "Create and freeze embryos with your partner",
  steps: [
    medicalHistoryStep,
    understandStep,
    knowYourNumbersStep,
    optimizeStep,
    insuranceStep,
    findCareStep,
    startCycleStep,
  ],
};
